function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useCallback } from 'react';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { dhNewCircleLargeFilled, vsGripper, vsTrash } from '@deephaven/icons';
import { Button, DragUtils, Tooltip } from '@deephaven/components';
import { TableUtils } from '@deephaven/jsapi-utils';
import Log from '@deephaven/log';
import "./ConditionalFormattingMenu.css";
import { FormatterType, BooleanCondition, formatRHV, getBackgroundForStyleConfig, getColorForStyleConfig, getShortLabelForConditionType, NumberCondition, StringCondition } from "./ConditionalFormattingUtils.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var log = Log.module('ConditionalFormattingMenu');
var DEFAULT_CALLBACK = () => undefined;
function getRuleValue(config) {
  var _formatRHV;
  var type = config.leftHandValue.type;

  // Null/not-null conditions have no value to display
  if (config.condition === StringCondition.IS_NULL || config.condition === StringCondition.IS_NOT_NULL) {
    return '';
  }

  // Boolean IS_TRUE/IS_FALSE have no RHV; IS_EQUAL/IS_NOT_EQUAL do
  if (TableUtils.isBooleanType(type) && (config.condition === BooleanCondition.IS_TRUE || config.condition === BooleanCondition.IS_FALSE)) {
    return '';
  }
  if (!TableUtils.isNumberType(type) && !TableUtils.isCharType(type) && !TableUtils.isStringType(type) && !TableUtils.isDateType(type) && !TableUtils.isBooleanType(type)) {
    throw new Error("Invalid column type ".concat(type, " in getRuleValue"));
  }
  var quote = TableUtils.isStringType(type) ? '"' : '';
  return (_formatRHV = formatRHV(config.rightHandValue, quote)) !== null && _formatRHV !== void 0 ? _formatRHV : '';
}
function getRuleTitle(config) {
  var _config$leftHandValue = config.leftHandValue,
    lhvName = _config$leftHandValue.name,
    lhvType = _config$leftHandValue.type;
  var formattedColumns = config.formattedColumns;
  var prefix = formattedColumns.length > 0 ? "[".concat(formattedColumns.map(c => c.name).join(', '), "] = ") : '';
  if (TableUtils.isNumberType(lhvType) && config.condition === NumberCondition.IS_BETWEEN) {
    return "".concat(prefix).concat(config.start, " < ").concat(lhvName, " < ").concat(config.end);
  }
  return "".concat(prefix).concat(lhvName, " ").concat(getShortLabelForConditionType(lhvType, config.condition), " \n    ").concat(getRuleValue(config));
}
function ConditionalFormattingMenu(props) {
  var _props$rules = props.rules,
    rules = _props$rules === void 0 ? [] : _props$rules,
    _props$onChange = props.onChange,
    onChange = _props$onChange === void 0 ? DEFAULT_CALLBACK : _props$onChange,
    _props$onCreate = props.onCreate,
    onCreate = _props$onCreate === void 0 ? DEFAULT_CALLBACK : _props$onCreate,
    _props$onSelect = props.onSelect,
    onSelect = _props$onSelect === void 0 ? DEFAULT_CALLBACK : _props$onSelect;
  var handleRuleClick = useCallback((e, rule, index) => {
    e.stopPropagation();
    log.debug('Rule clicked', rule, index);
    onSelect(index);
  }, [onSelect]);
  var handleDeleteClick = useCallback((e, rule, index) => {
    e.stopPropagation();
    log.debug('Delete button clicked', rule, index);
    var updatedRules = [...rules];
    updatedRules.splice(index, 1);
    onChange(updatedRules);
  }, [onChange, rules]);
  var handleDragHandlerClick = useCallback(e => {
    e.stopPropagation();
  }, []);
  var handleDragEnd = useCallback(result => {
    DragUtils.stopDragging();

    // if dropped outside the list
    if (result.destination == null) {
      return;
    }
    var sourceIndex = result.source.index;
    var destinationIndex = result.destination.index;
    var updatedRules = [...rules];
    var sourceInput = rules[sourceIndex];
    updatedRules.splice(sourceIndex, 1);
    updatedRules.splice(destinationIndex, 0, sourceInput);
    onChange(updatedRules);
  }, [onChange, rules]);

  // Display list of rules
  return /*#__PURE__*/_jsxs("div", {
    className: "conditional-formatting-rules",
    children: [/*#__PURE__*/_jsx(DragDropContext, {
      onDragStart: DragUtils.startDragging,
      onDragEnd: handleDragEnd,
      children: /*#__PURE__*/_jsx(Droppable, {
        droppableId: "droppable-custom-columns",
        children: (provided, snapshot) => /*#__PURE__*/_jsxs("div", _objectSpread(_objectSpread({
          ref: provided.innerRef
          // eslint-disable-next-line react/jsx-props-no-spreading
        }, provided.droppableProps), {}, {
          className: classNames('droppable-container', {
            dragging: snapshot.draggingFromThisWith
          }),
          children: [rules.length === 0 && /*#__PURE__*/_jsx("div", {
            className: "text-muted pl-2",
            children: "No formats defined"
          }), rules.map((rule, index) => /*#__PURE__*/_jsx(Draggable
          // eslint-disable-next-line react/no-array-index-key
          , {
            draggableId: "".concat(index, "-").concat(rule.type),
            index: index,
            disableInteractiveElementBlocking: true,
            children:
            // eslint-disable-next-line @typescript-eslint/no-shadow
            (provided, snapshot) => /*#__PURE__*/_jsx("div", _objectSpread(_objectSpread({
              role: "menuitem",
              tabIndex: 0,
              onClick: e => handleRuleClick(e, rule, index),
              className: classNames('draggable-container', {
                dragging: snapshot.isDragging
              }),
              ref: provided.innerRef
              // eslint-disable-next-line react/jsx-props-no-spreading
            }, provided.draggableProps), {}, {
              children: /*#__PURE__*/_jsx("div", {
                className: "conditional-formatting-list-item",
                children: /*#__PURE__*/_jsxs("div", {
                  className: "formatting-item",
                  children: [/*#__PURE__*/_jsx("div", {
                    className: "rule-icon",
                    children: /*#__PURE__*/_jsx("span", {
                      className: "rule-icon-bg",
                      style: {
                        backgroundColor: getBackgroundForStyleConfig(rule.config.style),
                        color: getColorForStyleConfig(rule.config.style)
                      },
                      children: rule.type === FormatterType.ROWS ? 'row' : 'col'
                    })
                  }), /*#__PURE__*/_jsx("div", {
                    className: "rule-title",
                    children: getRuleTitle(rule.config)
                  }), /*#__PURE__*/_jsx(Button, {
                    kind: "ghost",
                    className: "ml-1 px-2",
                    onClick: e => handleDeleteClick(e, rule, index),
                    icon: vsTrash,
                    tooltip: "Delete rule"
                  }), /*#__PURE__*/_jsxs("button", _objectSpread(_objectSpread({
                    type: "button",
                    className: "btn btn-link btn-link-icon px-2 btn-drag-handle",
                    onClick: handleDragHandlerClick
                    // eslint-disable-next-line react/jsx-props-no-spreading
                  }, provided.dragHandleProps), {}, {
                    children: [/*#__PURE__*/_jsx(Tooltip, {
                      children: "Drag to re-order"
                    }), /*#__PURE__*/_jsx(FontAwesomeIcon, {
                      icon: vsGripper
                    })]
                  }))]
                })
              })
            }))
          }, "".concat(index, "-").concat(rule.type))), provided.placeholder]
        }))
      })
    }), /*#__PURE__*/_jsx("hr", {}), /*#__PURE__*/_jsx(Button, {
      kind: "ghost",
      onClick: onCreate,
      icon: dhNewCircleLargeFilled,
      children: "Add New Rule"
    })]
  });
}
export default ConditionalFormattingMenu;
//# sourceMappingURL=ConditionalFormattingMenu.js.map